<script setup lang="ts">
const router = useRouter()
const localePath = useLocalePath()
// two ways to use it: as a route stacked on its parent route (a person on the
// people list), where closing navigates back to the parent -- or, without a
// parent route, as an overlay on whatever page is showing, where closing is
// left to the parent component through `close`
const props = defineProps<{
  parentRoute?: {
    name: string
  }
}>()

const emit = defineEmits<{
  close: []
}>()


const isLocked = useScrollLock(import.meta.client ? document.body : null)

const showMenu = useState('showMenu')
const showSearch = useState('showSearch')
const keepMenuOnNavigation = useState('keepMenuOnNavigation', () => false)

// rendered from the start (and faded in with `appear`) so that a modal landed
// on directly is server rendered like any other page
const visible = ref(true)
onMounted(() => {
  // FIXME
  // isLocked.value = true
  // the menu stacks on top of the modal, so opening one closes the other
  showMenu.value = false
})

const fadeOut = async () => {
  visible.value = false
  await new Promise(resolve => setTimeout(resolve, 220))
}

if (props.parentRoute) {
  onBeforeRouteLeave(fadeOut)
} else {
  // an overlay is not a route, so a navigation underneath it (a link inside
  // the modal, the browser's back button) closes it the same way leaving the
  // route version does
  const removeGuard = router.beforeEach(async () => {
    if (visible.value) await close()
  })
  onScopeDispose(removeGuard)
}

async function close() {
  if (props.parentRoute) {
    const parentRoutePath = localePath({ name: props.parentRoute.name })
    if (router.currentRoute.value.path !== parentRoutePath) {
      router.push(parentRoutePath)
    }
    return
  }

  await fadeOut()
  emit('close')
}

// only closes on escape when nothing is stacked on top of it (the media
// lightbox, the menu, the search)
useEscapeLayer(true, close)

// clicking the page behind the modal closes it — but not while an overlay on
// top of it is open, and not on the header, whose buttons open those overlays.
// the media lightbox portals to the body, so it lands outside the modal in the
// DOM while sitting on top of it on screen: it marks its layers instead.
const ignore = ['header', '[data-overlay-layer]']

const content = useTemplateRef<HTMLElement>('content')
onClickOutside(content, () => {
  if (showSearch.value || showMenu.value) return
  close()
}, { ignore })

// the menu stacks on top of the modal, so opening it closes the modal
// underneath — the flag keeps that navigation from closing the menu again.
// the overlay version closes without navigating, so there is nothing to keep
// the menu open through
watch(showMenu, (open) => {
  if (!open) return
  if (props.parentRoute) keepMenuOnNavigation.value = true
  close()
})

const contentTarget = useTemplateRef<HTMLElement>('contentTarget')
onClickOutside(contentTarget, () => {
  if (showSearch.value || showMenu.value) return
  close()
}, { ignore })
</script>

<template>
  <Transition
    appear
    enter-active-class="transition duration-150 ease-out"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="visible"
      ref="content"
      class="fixed z-100 inset-0 overflow-scroll overscroll-contain flex justify-end items-start bg-black/05 backdrop-grayscale-05"
    >
      <div 
        ref="contentTarget"
        class="container-d top-0 right-0 bg-white shadow-[0_0_1em_rgba(0,0,0,0.05)]"
      >
        <!-- `close` fades the modal out before it leaves, so a close control
             inside it goes through here rather than closing the parent directly -->
        <slot :close="close" />
      </div>
    </div>
  </Transition>
</template>